import { useMemo, useState, useEffect } from "react";
import { formatMomentDate } from "../../utils/dateHelper";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import PreviewModal from "../../components/modals/previewModal";
import { fetchMapLocations, updateMapStatus, reorderMapLocations, reorderMapFiles } from "../../redux/slice/mapSlice";
import ConfirmModal from "../../components/withModal/ConfirmModal";
import { usePermission } from "../../hooks/usePermission";
import CustomTable from "../../components/table/CustomTable";
import StatusChip from "../../components/statusChip/StatusChip";
import { useToast } from "../../context/TostContext";

const MODULE = "map_jantri";

const ListSection = ({
  data,
  currentPage,
  currentMapType,
  mapList,
  setSearchQuery,
  setFilterValues,
  setSelectedItem,
  setShowEditModal,
  setIsViewOnly,
  limit,
  setCurrentPage,
  isFileLevel,
  searchQuery,
  filterValues,
  isLoading,
  isInitialLoad,
  pagination,
  onDeleteMap,
  onRestoreMap,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const toast = useToast();
  const { hasPermission } = usePermission();
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [previewFileUrl, setPreviewFileUrl] = useState(null);
  const [modalItem, setModalItem] = useState(null);
  const [localData, setLocalData] = useState([]);

  useEffect(() => {
    setLocalData(data || []);
  }, [data]);

  const clearSearchAndNavigate = async (item) => {
    await dispatch(
      fetchMapLocations({
        map_type_id: currentMapType.id,
        parent_id: item.id,
        page: 1,
        limit: 10,
        direction: "forward",
        mode: "navigate",
      })
    );

    setSearchQuery("");
    setCurrentPage(1);

    setFilterValues(prev => ({
      ...prev,
      search: "",
    }));
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    if (!hasPermission(MODULE, "update")) return;

    const items = Array.from(localData);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setLocalData(items.map((item, index) => ({
      ...item,
      sequence: (currentPage - 1) * limit + index + 1
    })));

    const new_sequence = (currentPage - 1) * limit + result.destination.index + 1;
    if (isFileLevel) {
      dispatch(reorderMapFiles({ id: reorderedItem.id, new_sequence }));
    } else {
      dispatch(reorderMapLocations({ id: reorderedItem.id, new_sequence }));
    }
  };

  const columns = useMemo(() => {
    const cols = [
      {
        id: "sr_no",
        header: "SR NO.",
        cell: (_, row, index) => (currentPage - 1) * limit + index + 1
      },
      {
        id: "sequence",
        header: "SEQUENCE",
        cell: (_, row) => row.sequence || 0
      },
      {
        id: "name",
        header: isFileLevel ? "NAME" : (mapList?.next_level_label?.toUpperCase() || "NAME"),
        cell: (_, row) => !isFileLevel ? row.name : row.title || "-"
      }
    ];

    if (!isFileLevel) {
      cols.push(
        {
          id: "status",
          header: "STATUS",
          cell: (_, row) => (
            <StatusChip
              bg={row.is_active ? "success" : "danger"}
              pill
              title={row.is_active ? "Active" : "Inactive"}
              className={hasPermission(MODULE, "update") ? "cursor-pointer" : ""}
              onClick={(e) => {
                e.stopPropagation();
                if (hasPermission(MODULE, "update")) {
                  setModalItem(row);
                  setShowModal(true);
                }
              }}
            />
          )
        },
        {
          id: "created",
          header: "CREATED ON",
          cell: (_, row) => formatMomentDate(row.createdAt, "DD-MM-YYYY") || "-"
        }
      );
    }
    return cols;
  }, [isFileLevel, mapList?.next_level_label, currentPage, limit, hasPermission]);

  const actionList = useMemo(() => [
    hasPermission(MODULE, "read") && {
      title: "View",
      handler: (row) => {
        if (!isFileLevel) {
          setSelectedItem(row);
          setIsViewOnly(true);
          setShowEditModal(true);
        } else {
          const fileUrl = row.file_url;
          if (fileUrl?.toLowerCase().endsWith(".pdf")) {
            window.open(fileUrl, "_blank", "noopener,noreferrer");
          } else {
            setPreviewFileUrl(fileUrl);
            setShowViewModal(true);
          }
        }
      }
    },
    hasPermission(MODULE, "update") && {
      title: "Edit",
      handler: (row) => {
        setSelectedItem(row);
        setShowEditModal(true);
      }
    },
    hasPermission(MODULE, "update") && isFileLevel && {
      title: "Georeference",
      handler: (row) => {
        const fileUrl = row.file_url || "";
        const isImage = fileUrl.match(/\.(jpg|jpeg|png|webp)$/i);
        if (isImage) {
          navigate(`/maps/${currentMapType.code.toLowerCase()}/georeference/${row.id}`);
        } else {
          toast.error("Georeferencing is only supported for image formats (PNG, JPG, JPEG).");
        }
      }
    },
    !isFileLevel && hasPermission(MODULE, "update") && {
      title: (row) => row.is_active ? "Deactivate" : "Activate",
      handler: (row) => {
        setModalItem(row);
        setShowModal(true);
      }
    },
    // Soft delete, and its undo. A row only ever offers one of the two, decided
    // per row rather than per list, because "Show deleted" mixes live and
    // deleted sheets together.
    isFileLevel && hasPermission(MODULE, "delete") && {
      title: (row) => (row.deleted_at ? "Restore" : "Delete"),
      handler: (row) => (row.deleted_at ? onRestoreMap?.(row) : onDeleteMap?.(row)),
    }
  ].filter(Boolean), [hasPermission, isFileLevel, setSelectedItem, setIsViewOnly, setShowEditModal, currentMapType, navigate, toast, onDeleteMap, onRestoreMap]);

  const confirmChange = () => {
    if (!modalItem) return;

    dispatch(
      updateMapStatus({
        id: modalItem.id,
        is_active: !modalItem.is_active,
      })
    ).then((res) => {
      if (res?.payload?.success) {
        dispatch(
          fetchMapLocations({
            map_type_id: currentMapType.id,
            parent_id: mapList?.parent_id || null,
            page: 1,
            limit,
            direction: "forward",
            mode: "auto",
            search: "",
          })
        );
      }
    });

    setShowModal(false);
    setModalItem(null);
  };

  const isTableLoading = !isInitialLoad && (searchQuery !== filterValues.search || (isLoading && (filterValues.search || searchQuery)));

  return (
    <>
      <CustomTable
        data={localData}
        columns={columns}
        loading={isTableLoading}
        emptyMessage="No map locations found"
        keyField="id"
        actionList={actionList}
        isDragable={hasPermission(MODULE, "update")}
        onDragEnd={onDragEnd}
        rowClick={!isFileLevel ? (row) => clearSearchAndNavigate(row) : undefined}
        pagination={pagination}
      />

      <ConfirmModal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setModalItem(null);
        }}
        title={modalItem?.is_active ? "Deactivate Location" : "Activate Location"}
        message={
          modalItem?.is_active
            ? "This location will be deactivated. Do you want to continue?"
            : "This location will be activated. Do you want to continue?"
        }
        onConfirm={confirmChange}
      />

      {showViewModal && previewFileUrl && (
        <PreviewModal
          show={showViewModal}
          onHide={() => {
            setShowViewModal(false);
            setPreviewFileUrl(null);
          }}
          previewMedia={[{
            url: previewFileUrl,
            type: previewFileUrl?.match(/\.(jpg|jpeg|png|webp|gif)$/i) ? "image" : "document",
            title: "View File"
          }]}
        />
      )}
    </>
  );
};

export default ListSection;
